import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { GpoCommissionType } from './types';
import { getGpoOrdersEndpointIdentifier } from './use-get-gpo-orders-api';

const endpoint = 'ums/v1/cms/reconciliation/order-terms';

type TData = unknown;
type TError = AxiosError;
type TVariables = {
  orderId: number;
  commissionType: GpoCommissionType;
  /** Required for percent/fixed; ignored (omit) for `waived`. */
  commissionValue?: number;
  reason: string;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useOverrideGpoOrderCommissionApi(options?: Options): TReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ orderId, ...body }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.patch(`${endpoint}/${orderId}`, body);
      return data;
    } catch (error: any) {
      toast({
        description: error.response?.data?.message || 'Failed to update commission',
        status: 'error',
      });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${endpoint}/override`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getGpoOrdersEndpointIdentifier]);
      toast({ description: 'Commission updated', status: 'success' });
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
